import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { TruckIcon } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Base_Url, priceEstimatorPublic, getpincodePublic } from '../../config/config';
import axios from 'axios';
import { toast } from 'react-toastify';
import ShippingEstimateModal from '../b2c/models/estimateModelSignup';

const formSchema = z.object({
    pickupPincode: z.string().min(1, 'Required').length(6, 'Must be 6 digits'),
    dropPincode: z.string().min(1, 'Required').length(6, 'Must be 6 digits'),
    length: z.string().min(1, 'Required').transform(val => Number(val)),
    width: z.string().min(1, 'Required').transform(val => Number(val)),
    height: z.string().min(1, 'Required').transform(val => Number(val)),
    weight: z.string().min(1, 'Required').transform(val => Number(val)),
    weightUnit: z.enum(['gram', 'kg']),
    prepaid: z.string().min(1, 'Required'),
    codAmount: z.string().optional()
        .refine(val => val === undefined || val === '' || Number(val) > 0, {
            message: 'Must be > 0',
            path: ['codAmount']
        })
});

const ShippingRateCalculator = () => {
    const [pickupCity, setPickupCity] = useState('');
    const [dropCity, setDropCity] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [prepaidValue, setPrepaidValue] = useState('');
    const [data, setData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { register, handleSubmit, reset, watch, setValue, clearErrors, formState: { errors } } = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: { weightUnit: 'gram', prepaid: '' }
    });

    const handlePrepaidChange = (e) => setPrepaidValue(e.target.value);

    const token = localStorage.getItem("token") || "eyJ1c2VyX2lkIjoiNyIsImV4cGlyZXNfYXQiOjE3Mjc1MTc2Nzd9";
    const cus_id = localStorage.getItem("cus_id") || "7";
    const headers = useMemo(() => ({ token, cus_id, "Content-Type": "application/json" }), [token, cus_id]);

    const pickupPincode = watch('pickupPincode');
    const dropPincode = watch('dropPincode');

    const clearCity = useCallback((type) => {
        type === 'pickup' ? setPickupCity('') : setDropCity('');
    }, []);

    const fetchPincodeData = useCallback(async (pincode, type) => {
        try {
            const res = await fetch(`${Base_Url}${getpincodePublic}?pincode=${pincode}`, { method: 'GET', headers });
            const d = await res.json();
            if (d?.status === "SUCCESS") {
                const city = d.data[0].city.charAt(0).toUpperCase() + d.data[0].city.slice(1).toLowerCase();
                type === 'pickup' ? setPickupCity(city) : setDropCity(city);
            } else {
                clearCity(type);
                toast.error("Failed to fetch city data");
            }
        } catch {
            toast.error("Error fetching pincode data");
        }
    }, [headers, clearCity]);

    const handlePincodeChange = useCallback((e, type) => {
        const filtered = e.target.value.replace(/[^0-9]/g, '');
        const field = type === 'pickup' ? 'pickupPincode' : 'dropPincode';
        setValue(field, filtered);
        clearCity(type);
        if (filtered.length === 6) { clearErrors(field); fetchPincodeData(filtered, type); }
    }, [setValue, clearErrors, fetchPincodeData, clearCity]);

    useEffect(() => {
        pickupPincode?.length === 6 ? fetchPincodeData(pickupPincode, 'pickup') : clearCity('pickup');
    }, [pickupPincode, fetchPincodeData, clearCity]);

    useEffect(() => {
        dropPincode?.length === 6 ? fetchPincodeData(dropPincode, 'drop') : clearCity('drop');
    }, [dropPincode, fetchPincodeData, clearCity]);

    const onSubmit = async (formData) => {
        setIsProcessing(true);
        const requestBody = {
            from_pincode: formData.pickupPincode,
            to_pincode: formData.dropPincode,
            length: parseFloat(formData.length),
            width: parseFloat(formData.width),
            height: parseFloat(formData.height),
            weight: parseFloat(formData.weight) / 1000,
            shipment_type: formData.shipmentType,
            declared_value: formData.declaredValue ? parseFloat(formData.declaredValue) : "",
            payment_mode: formData.prepaid,
            cod_amount: formData.prepaid === 'cod' ? parseFloat(formData.cod) : "",
        };
        try {
            const response = await axios.post(Base_Url + priceEstimatorPublic, requestBody, { headers });
            if (response.data.status === "success") {
                setData(response.data);
                reset();
                setIsModalOpen(true);
                toast.success(response.data.message);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const inputCls = (err) =>
        `w-full px-3 py-1.5 text-xs border ${err ? 'border-red-400' : 'border-[#ede8de]'} rounded-md bg-[#fafaf8] focus:outline-none focus:ring-1 focus:ring-[#2b7de9] focus:border-[#2b7de9] transition placeholder-gray-400`;

    const labelCls = "block text-xs font-600 text-[#1a1a2e] mb-1";

    return (
        <section style={{ padding: 0, margin: 0 }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
                .rate-calc * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

                .rate-topbar {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 7px 14px;
                    background: #f9f7f2;
                    border-bottom: 1px solid #ede8de;
                }
                .rate-topbar-dot { width: 10px; height: 10px; border-radius: 50%; display:inline-block; }
                .rate-dot-red    { background: #ff6058; }
                .rate-dot-yellow { background: #ffbd2e; }
                .rate-dot-green  { background: #28c840; }
                .rate-topbar-label {
                    margin-left: 8px; font-size: 0.7rem; font-weight: 600;
                    color: #aaa; letter-spacing: 0.5px; text-transform: uppercase;
                }

                .rate-body {
                    background: #ffffff;
                    padding: 16px 20px 20px;
                    width: 100%;
                }

                .rate-section-title {
                    font-size: 0.65rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.6px;
                    color: #aaa;
                    margin-bottom: 8px;
                    margin-top: 14px;
                    border-bottom: 1px solid #f0ece3;
                    padding-bottom: 4px;
                }
                .rate-section-title:first-child { margin-top: 0; }

                .rate-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
                .rate-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
                .rate-grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 8px; }

                @media (max-width: 640px) {
                    .rate-grid-2 { grid-template-columns: 1fr; }
                    .rate-grid-3 { grid-template-columns: 1fr 1fr; }
                    .rate-grid-4 { grid-template-columns: 1fr 1fr; }
                }

                .rate-input {
                    width: 100%; padding: 6px 10px; font-size: 0.75rem;
                    border: 1px solid #ede8de; border-radius: 6px;
                    background: #fafaf8; color: #1a1a2e;
                    outline: none; transition: border 0.15s, box-shadow 0.15s;
                }
                .rate-input::placeholder { color: #bbb; }
                .rate-input:focus { border-color: #2b7de9; box-shadow: 0 0 0 2px rgba(43,125,233,0.1); }
                .rate-input.err { border-color: #f87171; }

                .rate-label { display: block; font-size: 0.72rem; font-weight: 600; color: #1a1a2e; margin-bottom: 4px; }

                .rate-city-tag {
                    font-size: 0.68rem; font-weight: 600; color: #2b7de9;
                    margin-top: 3px; display: block;
                }
                .rate-err { font-size: 0.65rem; color: #ef4444; margin-top: 2px; display: block; }

                .rate-submit {
                    width: 100%; margin-top: 16px;
                    padding: 9px 0; font-size: 0.8rem; font-weight: 700;
                    background: #1a1a2e; color: #ffffff;
                    border: none; border-radius: 8px; cursor: pointer;
                    display: flex; align-items: center; justify-content: center; gap: 7px;
                    letter-spacing: 0.3px;
                    transition: background 0.2s, box-shadow 0.2s;
                }
                .rate-submit:hover:not(:disabled) { background: #2b7de9; box-shadow: 0 4px 14px rgba(43,125,233,0.25); }
                .rate-submit:disabled { opacity: 0.6; cursor: not-allowed; }

                .rate-features {
                    display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
                    margin-top: 16px;
                }
                @media (max-width: 640px) { .rate-features { grid-template-columns: 1fr; } }

                .rate-feature-card {
                    background: #f9f7f2; border: 1px solid #ede8de;
                    border-radius: 8px; padding: 10px 12px; text-align: center;
                }
                .rate-feature-card h3 { font-size: 0.72rem; font-weight: 700; color: #1a1a2e; margin: 0 0 2px; }
                .rate-feature-card p  { font-size: 0.65rem; color: #888; margin: 0; }

                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>


            <div className="customers-section mt-16 ">
                <h2 className="customers-title">Price Estimator</h2>
                <p className="customers-subtitle">
                    Get an instant estimate for your shipping costs
                </p>
                <div className="rate-calc mt-16">
                    {/* Browser bar */}
                    <div className="rate-topbar">
                        <span className="rate-topbar-dot rate-dot-red" />
                        <span className="rate-topbar-dot rate-dot-yellow" />
                        <span className="rate-topbar-dot rate-dot-green" />
                        <span className="rate-topbar-label">Rate Calculator</span>
                    </div>

                    <div className="rate-body">
                        <form onSubmit={handleSubmit(onSubmit)}>

                            {/* Pincodes */}
                            <div className="rate-section-title">Origin &amp; Destination</div>
                            <div className="rate-grid-2">
                                <div>
                                    <label className="rate-label">Origin Pincode *</label>
                                    <input
                                        className={`rate-input${errors.pickupPincode ? ' err' : ''}`}
                                        type="text" maxLength="6" placeholder="6-digit pincode"
                                        {...register('pickupPincode')}
                                        onChange={(e) => handlePincodeChange(e, 'pickup')}
                                    />
                                    {errors.pickupPincode
                                        ? <span className="rate-err">{errors.pickupPincode.message}</span>
                                        : pickupCity && <span className="rate-city-tag">📍 {pickupCity}</span>}
                                </div>
                                <div>
                                    <label className="rate-label">Destination Pincode *</label>
                                    <input
                                        className={`rate-input${errors.dropPincode ? ' err' : ''}`}
                                        type="text" maxLength="6" placeholder="6-digit pincode"
                                        {...register('dropPincode')}
                                        onChange={(e) => handlePincodeChange(e, 'drop')}
                                    />
                                    {errors.dropPincode
                                        ? <span className="rate-err">{errors.dropPincode.message}</span>
                                        : dropCity && <span className="rate-city-tag">📍 {dropCity}</span>}
                                </div>
                            </div>

                            {/* Weight + Dimensions */}
                            <div className="rate-section-title">Package Details</div>
                            <div className="rate-grid-4">
                                <div>
                                    <label className="rate-label">Weight (g) *</label>
                                    <input
                                        className={`rate-input${errors.weight ? ' err' : ''}`}
                                        type="number" step="0.01" placeholder="0"
                                        {...register('weight')}
                                    />
                                    {errors.weight && <span className="rate-err">{errors.weight.message}</span>}
                                </div>
                                <div>
                                    <label className="rate-label">Length (cm) *</label>
                                    <input
                                        className={`rate-input${errors.length ? ' err' : ''}`}
                                        type="number" step="0.01" placeholder="0"
                                        {...register('length')}
                                    />
                                    {errors.length && <span className="rate-err">{errors.length.message}</span>}
                                </div>
                                <div>
                                    <label className="rate-label">Width (cm) *</label>
                                    <input
                                        className={`rate-input${errors.width ? ' err' : ''}`}
                                        type="number" step="0.01" placeholder="0"
                                        {...register('width')}
                                    />
                                    {errors.width && <span className="rate-err">{errors.width.message}</span>}
                                </div>
                                <div>
                                    <label className="rate-label">Height (cm) *</label>
                                    <input
                                        className={`rate-input${errors.height ? ' err' : ''}`}
                                        type="number" step="0.01" placeholder="0"
                                        {...register('height')}
                                    />
                                    {errors.height && <span className="rate-err">{errors.height.message}</span>}
                                </div>
                            </div>

                            {/* Payment */}
                            <div className="rate-section-title">Payment</div>
                            <div className="rate-grid-2">
                                <div>
                                    <label className="rate-label">Payment Method *</label>
                                    <select
                                        className={`rate-input${errors.prepaid ? ' err' : ''}`}
                                        {...register('prepaid')}
                                        onChange={handlePrepaidChange}
                                    >
                                        <option value="">Select method</option>
                                        <option value="prepaid">Prepaid</option>
                                        <option value="cod">Cash on Delivery (COD)</option>
                                    </select>
                                    {errors.prepaid && <span className="rate-err">{errors.prepaid.message}</span>}
                                </div>
                                {prepaidValue === 'cod' && (
                                    <div>
                                        <label className="rate-label">COD Amount *</label>
                                        <input
                                            className={`rate-input${errors.codAmount ? ' err' : ''}`}
                                            type="number" step="0.01" placeholder="Enter amount"
                                            {...register('codAmount')}
                                        />
                                        {errors.codAmount && <span className="rate-err">{errors.codAmount.message}</span>}
                                    </div>
                                )}
                            </div>

                            {/* Submit */}
                            <button type="submit" className="rate-submit" disabled={isProcessing}>
                                {isProcessing ? (
                                    <>
                                        <svg className="spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                                            <path d="M12 2a10 10 0 0 1 10 10" />
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <TruckIcon size={14} />
                                        Get Shipping Estimate
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Feature cards */}
                        <div className="rate-features">
                            {[
                                { title: 'Real-time Rates', desc: 'Instant quotes from multiple carriers' },
                                { title: 'Accurate Pricing', desc: 'No hidden fees or surprises' },
                                { title: 'Global Coverage', desc: 'Ship to 200+ countries worldwide' },
                            ].map((f, i) => (
                                <div key={i} className="rate-feature-card">
                                    <h3>{f.title}</h3>
                                    <p>{f.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <ShippingEstimateModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                estimateData={data}
            />
        </section>
    );
};

export default ShippingRateCalculator;